import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { RevealOnScroll } from "../../marketingAgency/shared";
import {
  LEAKED_LEAD_CLOSING,
  LEAKED_LEAD_THESIS,
  MOBILE_DIVIDER,
  siteAHeader,
  SITE_A_STEPS,
  siteBHeader,
  SITE_B_STEPS,
} from "./leakedLeadTimelineContent";

const TimelineColumn = ({ header, steps, variant, testId }) => {
  const reduceMotion = useReducedMotion();
  const isLoss = variant === "loss";

  return (
    <div data-testid={testId}>
      <h3
        className={`text-sm font-mono font-semibold tracking-wider mb-4 px-1 ${
          isLoss ? "text-red-400/90" : "text-partner-accent"
        }`}
      >
        {header}
      </h3>
      <ol className="relative border-l border-partner ml-2 space-y-4">
        {steps.map((step, index) => (
          <motion.li
            key={step.title}
            initial={reduceMotion ? false : { opacity: 0, x: isLoss ? -12 : 12 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.35, delay: index * 0.08 }}
            className={`relative pl-6 pb-2 rounded-r-xl pr-4 py-3 ${
              step.highlight
                ? isLoss
                  ? "bg-red-500/10 border border-red-500/25"
                  : "bg-[#00D2FF]/10 border border-[#00D2FF]/25"
                : "bg-[#11151F]/80 border border-partner"
            }`}
          >
            <span
              className={`absolute -left-[5px] top-5 w-2.5 h-2.5 rounded-full ring-4 ring-[#080A0F] ${
                isLoss ? "bg-red-400" : "bg-[#00D2FF]"
              }`}
              aria-hidden
            />
            <p className="text-[11px] font-mono text-partner-muted mb-1">{step.time}</p>
            <p className="text-sm font-semibold text-partner mb-1">{step.title}</p>
            <p className="text-sm text-partner-muted font-body leading-relaxed">{step.body}</p>
          </motion.li>
        ))}
      </ol>
    </div>
  );
};

const LeakedLeadTimelineSection = ({ id = "leaked-lead" }) => (
  <section
    id={id}
    className="section-padding relative overflow-hidden border-y border-partner"
    data-testid="leaked-lead-timeline"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-4 max-w-3xl">
          {LEAKED_LEAD_THESIS.headline}
        </h2>
        <p className="text-partner-muted font-body max-w-3xl leading-relaxed mb-10">
          {LEAKED_LEAD_THESIS.subline}
        </p>
      </RevealOnScroll>

      <RevealOnScroll delay={0.08}>
        <div className="hidden lg:grid lg:grid-cols-2 gap-8 xl:gap-12">
          <TimelineColumn header={siteAHeader()} steps={SITE_A_STEPS} variant="loss" testId="timeline-site-a" />
          <TimelineColumn header={siteBHeader()} steps={SITE_B_STEPS} variant="win" testId="timeline-site-b" />
        </div>

        <div className="lg:hidden space-y-8">
          <TimelineColumn header={siteAHeader()} steps={SITE_A_STEPS} variant="loss" testId="timeline-site-a-mobile" />
          <p className="text-center text-sm font-mono text-partner-muted py-2">{MOBILE_DIVIDER}</p>
          <TimelineColumn header={siteBHeader()} steps={SITE_B_STEPS} variant="win" testId="timeline-site-b-mobile" />
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.12}>
        <blockquote className="mt-10 rounded-xl border border-partner bg-[#11151F] px-6 py-6 sm:px-8">
          <p className="text-lg sm:text-xl font-bold text-partner mb-3">{LEAKED_LEAD_CLOSING.headline}</p>
          <p className="text-sm sm:text-base text-partner-muted font-body leading-relaxed">
            {LEAKED_LEAD_CLOSING.body}
          </p>
          <p
            className="mt-4 pt-4 border-t border-partner text-sm sm:text-base text-partner font-semibold font-body leading-relaxed"
            data-testid="leaked-lead-agency-mirror"
          >
            {LEAKED_LEAD_CLOSING.agencyMirror}
          </p>
          <p
            className="mt-4 pt-4 border-t border-[#00D2FF]/25 text-sm sm:text-base text-partner-accent font-semibold font-body leading-relaxed"
            data-testid="leaked-lead-partner-exit"
          >
            {LEAKED_LEAD_CLOSING.partnerExit}
          </p>
        </blockquote>
      </RevealOnScroll>
    </div>
  </section>
);

export default LeakedLeadTimelineSection;
